import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import { Bot, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';

export default function Tutorial() {
  const navigate = useNavigate();
  const { selectedBusiness } = useBusiness();
  const [currentStep, setCurrentStep] = useState(() => {
    // Check if we're in the middle of a tutorial
    const saved = localStorage.getItem('tutorialStep');
    const tutorialActive = localStorage.getItem('tutorialActive');
    
    console.log('🔍 Tutorial initialization:', {
      saved,
      tutorialActive,
      pathname: window.location.pathname
    });
    
    // If on settings page and tutorial is active, force step 4
    if (window.location.pathname.includes('/settings') && tutorialActive === 'true') {
      console.log('📖 On settings page during tutorial, loading step 4');
      return 4;
    }
    
    if (tutorialActive === 'true' && saved) {
      // Tutorial is active, load the saved step
      console.log('📖 Loading tutorial step from storage:', saved);
      return parseInt(saved);
    }
    
    // No active tutorial, start from step 1
    console.log('🆕 Starting new tutorial from step 1');
    localStorage.setItem('tutorialActive', 'true');
    return 1;
  });
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [typedText, setTypedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const totalSteps = 4; // Will increase as we add more tutorial steps

  const tutorialMessages: Record<number, string> = {
    2: "This is your business profile, please tap the profile icon.",
    3: "Now tap the Business Settings button.",
    4: "Here you can edit all of your public business information like your business name, address, phone number and email. This is where you can also add your business logo and set your business hours."
  };

  // Save tutorial step to localStorage whenever it changes
  React.useEffect(() => {
    localStorage.setItem('tutorialStep', currentStep.toString());
    console.log('💾 Tutorial step saved to localStorage:', currentStep);
    console.log('📍 Current tutorial state:', {
      currentStep,
      isTyping,
      typedText: typedText.substring(0, 20) + '...',
      location: window.location.pathname
    });
  }, [currentStep]);

  // Typing animation effect
  React.useEffect(() => {
    const message = tutorialMessages[currentStep];
    if ((currentStep === 2 || currentStep === 3 || currentStep === 4) && !isTyping && message) {
      setIsTyping(true);
      setTypedText('');
      
      let index = 0;
      const typingInterval = setInterval(() => {
        if (index < message.length) {
          setTypedText(message.substring(0, index + 1));
          index++;
        } else {
          clearInterval(typingInterval);
          setIsTyping(false);
        }
      }, 30); // 30ms per character for smooth typing

      return () => clearInterval(typingInterval);
    }
  }, [currentStep]);

  const completeTutorial = async () => {
    if (!selectedBusiness?.id) return;

    try {
      // Mark tutorial as completed
      const { error } = await supabase
        .from('businesses')
        .update({
          tutorial_completed: true,
          tutorial_completed_at: new Date().toISOString()
        })
        .eq('id', selectedBusiness.id);

      if (error) {
        console.error('Error completing tutorial:', error);
        toast.error('Failed to complete tutorial');
        return;
      }

      console.log('✅ Tutorial marked as completed');
      
      // Clean up tutorial state
      localStorage.removeItem('tutorialStep');
      localStorage.removeItem('tutorialActive');
      
      toast.success('Tutorial completed! Redirecting...');
      
      // Redirect to main app with full page refresh
      setTimeout(() => {
        window.location.href = '/';
      }, 1500);
    } catch (error) {
      console.error('Error in completeTutorial:', error);
      toast.error('Failed to complete tutorial');
    }
  };

  const handleNextStep = () => {
    if (currentStep === 1) {
      // Trigger transition animation
      setIsTransitioning(true);
      // Move to step 2 after animation starts
      setTimeout(() => {
        setCurrentStep(2);
      }, 800); // Wait for fade out
    } else if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      // Last step completed, finish tutorial
      completeTutorial();
    }
  };

  // Listen for profile icon click to move to step 3
  React.useEffect(() => {
    if (currentStep === 2 && !isTyping) {
      const handleProfileClick = (e: MouseEvent) => {
        console.log('🔍 Click detected, target:', e.target);
        console.log('Profile icon clicked, moving to step 3');
        setCurrentStep(3);
      };

      // Add global click listener to detect clicks in the top-right area
      const handleGlobalClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        const rect = target.getBoundingClientRect();
        
        // Check if click is in the top-right profile area (rough coordinates)
        if (rect.right > window.innerWidth - 100 && rect.top < 100) {
          console.log('🎯 Click in profile area detected!');
          handleProfileClick(e);
        }
      };

      document.addEventListener('click', handleGlobalClick, true);
      return () => document.removeEventListener('click', handleGlobalClick, true);
    }
  }, [currentStep, isTyping]);

  // Prevent dropdown from closing on step 3
  React.useEffect(() => {
    if (currentStep === 3) {
      const preventClose = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        
        // Check if click is on our tutorial button (allow it)
        const isTutorialButton = target.closest('button')?.textContent?.includes('CLICK ME');
        if (isTutorialButton) {
          console.log('✅ Tutorial button clicked, allowing');
          return; // Don't prevent, allow it through
        }
        
        // Check if click is on profile icon or outside dropdown (would normally close it)
        const isProfileIcon = target.closest('[data-profile-menu-trigger]') || 
                             (target.getBoundingClientRect().right > window.innerWidth - 100 && 
                              target.getBoundingClientRect().top < 100);
        
        // Check if click is outside the dropdown area
        const isOutsideDropdown = !target.closest('[role="menu"]') && 
                                  !target.closest('[data-dropdown-menu]') &&
                                  !isProfileIcon;
        
        // Prevent clicks that would close the dropdown
        if (isProfileIcon || isOutsideDropdown) {
          console.log('🚫 Preventing dropdown close');
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          return false;
        }
      };

      document.addEventListener('mousedown', preventClose, true);
      document.addEventListener('click', preventClose, true);
      
      return () => {
        document.removeEventListener('mousedown', preventClose, true);
        document.removeEventListener('click', preventClose, true);
      };
    }
  }, [currentStep]);

  return (
    <>
      {/* Overlay to disable all clicks - must be very high z-index to cover navigation */}
      {currentStep === 2 && !isTyping ? (
        // Step 2: Overlay with cutout for profile icon
        <>
          <div 
            className="fixed inset-0 pointer-events-auto"
            style={{ 
              background: 'rgba(0, 0, 0, 0.5)',
              cursor: 'default',
              zIndex: 9999,
              clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 0, calc(100% - 4.5rem) 0.5rem, calc(100% - 4.5rem) 3rem, calc(100% - 1rem) 3rem, calc(100% - 1rem) 0.5rem, calc(100% - 4.5rem) 0.5rem)'
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          />
        </>
      ) : currentStep === 3 && !isTyping ? (
        // Step 3: Very high z-index overlay covering dropdown
        <>
          <div 
            className="fixed inset-0 pointer-events-auto"
            style={{ 
              background: 'rgba(0, 0, 0, 0.01)', // Almost transparent
              cursor: 'default',
              zIndex: 9999999 // Extremely high to be above dropdown
            }}
            onClick={(e) => {
              // Check if click is on Business Settings button area
              const clickX = e.clientX;
              const clickY = e.clientY;
              const windowWidth = window.innerWidth;
              
              // Business Settings button approximate area (adjust as needed)
              const buttonLeft = windowWidth - 240; // About 240px from right
              const buttonRight = windowWidth - 24; // About 24px from right
              const buttonTop = 128; // About 128px from top (8rem)
              const buttonBottom = 164; // About 164px from top (8rem + 2.25rem)
              
              const isBusinessSettingsClick = clickX >= buttonLeft && clickX <= buttonRight && 
                                             clickY >= buttonTop && clickY <= buttonBottom;
              
              if (!isBusinessSettingsClick) {
                // Not clicking Business Settings, block the click
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                return false;
              } else {
                console.log('✅ Business Settings button clicked!');
                // Navigate to Business Settings and move to step 4
                navigate('/settings/application-settings');
                setTimeout(() => {
                  setCurrentStep(4);
                }, 500);
                // Allow this click to pass through
              }
            }}
            onMouseDown={(e) => {
              // Same logic for mousedown
              const clickX = e.clientX;
              const clickY = e.clientY;
              const windowWidth = window.innerWidth;
              
              const buttonLeft = windowWidth - 240;
              const buttonRight = windowWidth - 24;
              const buttonTop = 128;
              const buttonBottom = 164;
              
              const isBusinessSettingsClick = clickX >= buttonLeft && clickX <= buttonRight && 
                                             clickY >= buttonTop && clickY <= buttonBottom;
              
              if (!isBusinessSettingsClick) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                return false;
              }
            }}
          />
        </>
      ) : currentStep === 4 ? (
        // Step 4: Full overlay blocking everything
        <div 
          className="fixed inset-0 pointer-events-auto"
          style={{ 
            background: 'rgba(0, 0, 0, 0.5)',
            cursor: 'default',
            zIndex: 9999
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }}
        />
      ) : (
        // Default: Full overlay
        <div 
          className="fixed inset-0 pointer-events-auto"
          style={{ 
            background: 'rgba(0, 0, 0, 0.5)',
            cursor: 'default',
            zIndex: 9999
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }}
        />
      )}
      
      {/* Conditional highlights and elements */}
      {currentStep === 2 && !isTyping && (
        <>
          {/* Blue highlight ring around profile icon - Only on step 2 */}
          <div 
            className="fixed pointer-events-none"
            style={{
                top: '0.25rem',
                right: '0.75rem',
                width: '2.75rem',
                height: '2.75rem',
                zIndex: 99998,
                borderRadius: '50%', // Perfect circle
                border: '3px solid #3B82F6',
                boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.3), 0 0 20px rgba(59, 130, 246, 0.5)',
                animation: 'pulse 2s ease-in-out infinite'
              }}
            />
        </>
      )}
      
      {/* Global styles for animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% {
            box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.3), 0 0 20px rgba(59, 130, 246, 0.5);
          }
          50% {
            box-shadow: 0 0 0 8px rgba(59, 130, 246, 0.2), 0 0 30px rgba(59, 130, 246, 0.7);
          }
        }
      `}</style>
      
      {/* Tutorial Modal - Absolute highest z-index */}
      <div 
        className="fixed inset-0 pointer-events-none"
        style={{ zIndex: 99999 }}
      >
        {/* Step 1: Welcome Modal */}
        {currentStep === 1 && (
          <div className={`flex items-center justify-center h-full transition-opacity duration-800 ${
            isTransitioning ? 'opacity-0' : 'opacity-100'
          }`}>
            <div className="pointer-events-auto max-w-lg w-full mx-4">
              <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
                {/* Nexus AI Avatar */}
                <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-8 text-center relative">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                  <div className="relative">
                    <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <Bot className="h-12 w-12 text-blue-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-white">Nexus AI</h2>
                  </div>
                </div>

                {/* Speech Bubble */}
                <div className="p-8">
                  <div className="bg-gray-50 rounded-2xl p-6 relative">
                    {/* Speech bubble arrow */}
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <div className="w-6 h-6 bg-gray-50 rotate-45"></div>
                    </div>
                    
                    <p className="text-gray-900 text-lg leading-relaxed relative z-10">
                      Welcome to Nexus! I will quickly show you around the app and then you can begin to explore it yourself.
                    </p>
                  </div>

                  {/* Action Button */}
                  <button
                    onClick={handleNextStep}
                    className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-200 flex items-center justify-center space-x-2"
                  >
                    <span>Let's Get Started</span>
                    <ArrowRight className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Nexus Icon - Positioned based on current step */}
        {(currentStep === 2 || currentStep === 3) && (
          <>
            {/* Animated Nexus Icon */}
            <div 
              className="fixed pointer-events-auto transition-all duration-500 ease-in-out"
              style={{
                top: currentStep === 2 ? '1rem' : '18rem',
                right: currentStep === 2 ? '5rem' : '1rem',
                transform: 'scale(1)',
                ...(currentStep === 2 && {
                  animation: 'slideFromCenter 1s ease-in-out forwards'
                })
              }}
            >
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                <Bot className="h-7 w-7 text-white" />
              </div>
            </div>
            
            {currentStep === 2 && (
              <style>{`
                @keyframes slideFromCenter {
                  0% {
                    top: 50%;
                    left: 50%;
                    right: auto;
                    transform: translate(-50%, -50%) scale(1.5);
                    opacity: 1;
                  }
                  100% {
                    top: 1rem;
                    right: 5rem;
                    left: auto;
                    transform: translate(0, 0) scale(1);
                    opacity: 1;
                  }
                }
              `}</style>
            )}
          </>
        )}

        {/* Step 2: Profile Instruction */}
        {currentStep === 2 && (
          <>

            {/* Speech Bubble pointing to profile icon */}
            <div 
              className="absolute pointer-events-auto animate-in fade-in slide-in-from-top-4 duration-500"
              style={{
                top: '4.5rem',
                right: '4rem',
                animationDelay: '400ms'
              }}
            >
              <div className="bg-white rounded-xl shadow-2xl p-4 max-w-xs relative">
                {/* Arrow pointing up */}
                <div className="absolute -top-2 right-8">
                  <div className="w-4 h-4 bg-white rotate-45"></div>
                </div>
                
                <p className="text-gray-900 text-sm leading-relaxed relative z-10">
                  {typedText}
                  {isTyping && (
                    <span className="inline-block w-0.5 h-4 bg-blue-600 ml-0.5 animate-pulse"></span>
                  )}
                </p>
              </div>
            </div>
          </>
        )}

        {/* Step 3: Highlight Business Settings */}
        {currentStep === 3 && (
          <>
            {/* Speech Bubble pointing to Business Settings */}
            <div 
              className="absolute pointer-events-auto animate-in fade-in slide-in-from-right-4 duration-500"
              style={{
                top: '16.5rem',
                right: '4rem',
                animationDelay: '200ms'
              }}
            >
              <div className="bg-white rounded-xl shadow-2xl p-4 max-w-xs relative">
                {/* Arrow pointing right */}
                <div className="absolute top-4 -right-2">
                  <div className="w-4 h-4 bg-white rotate-45"></div>
                </div>
                
                <p className="text-gray-900 text-sm leading-relaxed relative z-10">
                  {typedText}
                  {isTyping && (
                    <span className="inline-block w-0.5 h-4 bg-blue-600 ml-0.5 animate-pulse"></span>
                  )}
                </p>
              </div>
            </div>

            {/* Clickable Business Settings Button Highlight */}
            {!isTyping && createPortal(
              <button
                onMouseDown={(e) => {
                  console.log('🎯 MOUSEDOWN on highlight!');
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  console.log('🎯 CLICK on highlight!');
                  e.preventDefault();
                  e.stopPropagation();
                  
                  console.log('Setting step to 4...');
                  setCurrentStep(4);
                  
                  console.log('Navigating to Business Settings page...');
                  navigate('/settings/application-settings');
                }}
                onMouseEnter={() => console.log('👆 Mouse entered highlight button')}
                className="fixed"
                style={{
                  top: '7rem',
                  right: '1.75rem',
                  width: '13rem',
                  height: '2.75rem',
                  zIndex: 2147483647, // Maximum possible z-index
                  borderRadius: '0.5rem',
                  border: '6px solid #FF0000', // RED for debugging
                  boxShadow: '0 0 0 10px rgba(255, 0, 0, 0.5), 0 0 40px rgba(255, 0, 0, 0.8)',
                  backgroundColor: 'rgba(255, 0, 0, 0.7)', // BRIGHT RED for debugging
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: '700',
                  fontSize: '1rem',
                  pointerEvents: 'auto'
                }}
              >
                🎯 CLICK ME 🎯
              </button>,
              document.body
            )}
          </>
        )}

        {/* Step 4: Business Settings Page - Modal */}
        {currentStep === 4 && (
          <>
            {console.log('🎨 Rendering Step 4 modal')}
            <div className="flex items-center justify-center h-full" style={{ zIndex: 999999 }}>
              <div className="pointer-events-auto max-w-lg w-full mx-4">
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
                {/* Nexus AI Avatar */}
                <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-8 text-center relative">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                  <div className="relative">
                    <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <Bot className="h-12 w-12 text-blue-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-white">Nexus AI</h2>
                  </div>
                </div>

                {/* Speech Bubble */}
                <div className="p-8">
                  <div className="bg-gray-50 rounded-2xl p-6 relative">
                    {/* Speech bubble arrow */}
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <div className="w-6 h-6 bg-gray-50 rotate-45"></div>
                    </div>
                    
                    <p className="text-gray-900 text-base leading-relaxed relative z-10">
                      {typedText}
                      {isTyping && (
                        <span className="inline-block w-0.5 h-4 bg-blue-600 ml-0.5 animate-pulse"></span>
                      )}
                    </p>
                  </div>

                  {/* Action Button */}
                  {!isTyping && (
                    <button
                      onClick={handleNextStep}
                      className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-200 flex items-center justify-center space-x-2"
                    >
                      <span>Continue</span>
                      <ArrowRight className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          </>
        )}
      </div>
    </>
  );
}
